import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setDataToDB } from "../db/dbHelpers.js";
import {
  clearCredentials,
  setCredentials,
} from "../core/store/redux/slices/Auth-slice.js";
import useNetworkStatus from "./useNetworkStatus.js";
import ApiClient from "../apis/Api-client.js";
import { setAccessToken, getAccessToken, decodeToken } from "../apis/tokenManager.js";
import { END_POINT } from "../utils/End-points.js";
import { fetchPermissions } from "../apis/permission.service.js";

const registerDeviceForPush = (data) => {
  import("../config/firebase/messaging.js")
    .then((m) => m.registerDeviceForPush(data))
    .catch((err) => console.error("Push registration unavailable:", err));
};


const useUserDetails = (sessionUserKey, sessionHotelKey) => {
  const userKey = sessionUserKey || sessionStorage.getItem("user_key");
  const hotelKey = sessionHotelKey || sessionStorage.getItem("hotel_key");

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const { authApi } = ApiClient();
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();

  const fetchUserDetails = useCallback(async () => {
    if (decodeToken(getAccessToken())?.must_change_password) {
      setLoading(false);
      return;
    }

    if (!userKey) {
      dispatch(clearCredentials());
      setLoading(false);
      return;
    }

    try {
      const response = await authApi.get(
        END_POINT.USER.GET_USER_BY_FILTER,
        { user_key: userKey },
        "Error Fetching User Details"
      );

      if (response?.status === 200 && response?.data?.data) {
        const { company, role, trial, ...userData } = response.data.data;

        const commonData = {
          user: { ...userData, ...role },
          is_trial: company?.is_trial,
          trial,
        };

        const isCompanyScope = Array.isArray(company?.hotels) && role?.scope !== "hotel";

        if (isCompanyScope) {
          let selectedHotel = null;

          if (hotelKey) {
            selectedHotel =
              company.hotels?.find((h) => h.hotel_key === hotelKey) || null;

            if (!selectedHotel) {
              dispatch(clearCredentials());
              return;
            }

            try {
              const switchRes = await authApi.post(
                END_POINT.AUTH.SWITCH_HOTEL,
                { hotel_key: hotelKey }
              );
              setAccessToken(switchRes?.data?.data?.accessToken);
            } catch (error) {
              console.error("Failed to switch hotel token on bootstrap:", error);
            }
          }

          const adminData = {
            ...commonData,
            hotel: selectedHotel,
            company_id: company?.company_id,
            adminHotels: company.hotels,
          };

          dispatch(setCredentials(adminData));
          await setDataToDB("credentials", {
            ...adminData,
            isAuthenticated: true,
          });
          await fetchPermissions();
          registerDeviceForPush({
            user_key: commonData?.user?.user_key || userKey,
            hotel_key: selectedHotel?.hotel_key || hotelKey,
          });
        } else {
          const hotelObj = company?.hotel
            || (Array.isArray(company?.hotels) && hotelKey
              ? company.hotels.find((h) => h.hotel_key === hotelKey)
              : company?.hotels?.[0])
            || null;

          const userData = {
            ...commonData,
            company_id: company?.company_id,
            hotel: hotelObj,
          };

          dispatch(setCredentials(userData));
          await setDataToDB("credentials", {
            ...userData,
            isAuthenticated: true,
          });
          await fetchPermissions();
          registerDeviceForPush({
            user_key: commonData?.user?.user_key || userKey,
            hotel_key: userData?.hotel?.hotel_key || hotelKey,
          });
        }
      } else {
        dispatch(clearCredentials());
      }
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      const status = error?.response?.status;
      const mustChange = error?.response?.data?.must_change_password;
      if (status === 401 || (status === 403 && !mustChange)) {
        dispatch(clearCredentials());
      }
    } finally {
      setLoading(false);
    }
  }, [userKey, hotelKey, authApi, dispatch]);

  useEffect(() => {
    if (isOnline) fetchUserDetails();
  }, [isOnline, userKey, hotelKey]);

  return { loading, fetchUserDetails };
};

export default useUserDetails;
